import React, { useState, useEffect } from "react";
import { ColumnDef } from "@tanstack/react-table";
import styles from "./medicalIcdGroups.module.css";
import { Toaster } from "../../components/ui/toaster";
import DataTable from "../../shared/DataTable";
import { Button } from "../../components/ui/button";
import { MedicalIcdAxiosInstance } from "../../Network/medicalIcd";
import { Datum } from "../../interface/MedicalIcd";
import { isViewer } from "../../interface/RoleHelpers";
import FileUpload from "../../UploadFile/MedicalIcd";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { AlertTriangle } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

const Medicalicd: React.FC = () => {
  // =====================================
  // Main Data State
  // =====================================
  const [allItems, setAllItems] = useState<Datum[]>([]);

  // =====================================
  // Pagination State
  // =====================================
  const [searchInput, setSearchInput] = useState(""); // Immediate input value typed by the user
  const [search, setSearch] = useState(""); // Debounced value sent to the server
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // =====================================
  // Selected / Editing State
  // =====================================
  const [selectedIcd, setSelectedIcd] = useState<
    { id: string; code: string }[]
  >([]);
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const [loading, setLoading] = useState(true);

  // =====================================
  // Dialog & Modal Refs
  // =====================================
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [IcdDialogOpen, setIcdDialogOpen] = useState(false);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [warningDialog, setWarningDialog] = useState<{
    open: boolean;
    item: Datum | null;
    usageData: any;
    loadingUsage: boolean;
  }>({ open: false, item: null, usageData: null, loadingUsage: false });

  const { toast } = useToast();

  const handleOpenDialog = async (item: Datum) => {
    try {
      const response = await MedicalIcdAxiosInstance.get(
        `medical-group/icd/${item.id}`,
      );
      const fullData = response.data.data || response.data;
      setSelectedItem(fullData);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch group details:", error);
      toast({
        title: "Error",
        description: "Failed to load group details. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedItem(null);
    setShowAllLogs(false);
  };

  // =====================================
  // Advanced Search (server-side)
  // =====================================
  const [advancedSearchInput, setAdvancedSearchInput] = useState({
    groupName: "",
    icdCode: "",
    status: "",
  });

  const [advancedSearch, setAdvancedSearch] = useState({
    groupName: "",
    icdCode: "",
    status: "",
  });

  const clearFilters = () => {
    setAdvancedSearchInput({ groupName: "", icdCode: "", status: "" });
    setSearchInput("");
  };

  // =====================================
  // Error Handling
  // =====================================
  const getApiErrorMessage = (error: any): string => {
    if (!error) return "Unknown error";
    if (error.response?.data?.error?.message)
      return error.response.data.error.message;
    if (error.response?.data?.message) return error.response.data.message;
    if (error.message) return error.message;
    return "An unexpected error occurred.";
  };

  // =====================================
  // Debounce
  // =====================================
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);

    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      const cleanedIcd =
        advancedSearchInput.icdCode.trim().length >= 3
          ? advancedSearchInput.icdCode.trim()
          : "";

      setAdvancedSearch({
        groupName: advancedSearchInput.groupName.trim(),
        icdCode: cleanedIcd,
        status: advancedSearchInput.status,
      });

      setPage(1);
    }, 400);

    return () => clearTimeout(t);
  }, [advancedSearchInput]);

  const buildFilterParams = (): Record<string, any> => {
    const params: Record<string, any> = {
      includeCodes: true,
    };
    if (search) params.name = search;
    if (advancedSearch.groupName) params.name = advancedSearch.groupName;
    if (advancedSearch.icdCode) params.code = advancedSearch.icdCode;
    if (advancedSearch.status) params.active = advancedSearch.status === "Active";
    return params;
  };

  const hasActiveFilters = Boolean(
    search ||
      advancedSearch.groupName ||
      advancedSearch.icdCode ||
      advancedSearch.status
  );

  // =====================================
  // Fetch Functions (Server-side pagination + search + filters)
  // =====================================
  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, advancedSearch]);

  const fetchItems = async () => {
    try {
      setLoading(true);

      const params: Record<string, any> = {
        page,
        limit,
        ...buildFilterParams(),
      };

      const res = await MedicalIcdAxiosInstance.get("medical-group/icd", {
        params,
      });

      const responseData = res.data.data;
      const items = responseData?.items ?? [];

      setAllItems(items);
      setTotalPages(responseData?.totalPages ?? 1);
      setTotalItems(responseData?.total ?? items.length);

      const pages = responseData?.totalPages ?? 1;
      if (page > pages) {
        setPage(Math.max(1, pages));
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // =====================================
  // toggleActive Function
  // =====================================
  const toggleActive = async (row: Datum) => {
    if (row.isActive) {
      try {
        const res = await MedicalIcdAxiosInstance.get(
          `medical-group/icd/usage/${row.id}`,
        );
        const usageData = res.data.data;

        if (!usageData.isUsed || usageData.totalUsageCount === 0) {
          confirmToggle(row);
        } else {
          setWarningDialog({
            open: true,
            item: row,
            usageData: usageData,
            loadingUsage: false,
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description: getApiErrorMessage(error),
          variant: "destructive",
        });
      }
    } else {
      confirmToggle(row);
    }
  };

  const confirmToggle = async (row: Datum) => {
    try {
      await MedicalIcdAxiosInstance.patch(
        `medical-group/icd/${row.id}/activation`,
        { isActive: !row.isActive },
      );

      toast({
        variant: "success",
        title: "Success",
        description: `Status updated to ${!row.isActive ? "active" : "inactive"}`,
      });

      setAllItems((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, isActive: !item.isActive } : item,
        ),
      );
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const formatText = (text: string) => {
    if (!text) return "";

    return text
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // =====================================
  // Download Function (Backend Export per Item)
  // =====================================
  const handleDownload = async (item: Datum) => {
    if (isViewer()) {
      toast({
        title: "Error",
        description: "Access denied. Viewers are only allowed to view the system",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await MedicalIcdAxiosInstance.get(`medical-group/icd/${item.id}/export`, {
        responseType: 'blob',
      });
      
      const disposition: string = response.headers?.['content-disposition'] ?? '';
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const filename = match ? decodeURIComponent(match[1]) : `MedicalIcd_${item.name || "export"}.xlsx`;

      const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        variant: "success",
        title: "Export Finished",
        description: `"${item.name}" exported successfully.`,
      });
    } catch (error) {
      toast({ title: "Error", description: "Failed to download export file", variant: "destructive" });
    }
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query || query.trim().length < 3) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();

    if (!lowerText.includes(lowerQuery)) return text;

    const parts = text.split(new RegExp(`(${query})`, "gi"));

    return parts.map((part, index) =>
      part.toLowerCase() === lowerQuery ? (
        <span
          key={index}
          className="bg-yellow-50 text-yellow-900 px-1 rounded font-semibold"
          data-testid={`medicalIcd-page-highlight-match-${index}`}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const activeHighlightQuery =
    advancedSearch.icdCode.length >= 3
      ? advancedSearch.icdCode
      : search.length >= 3
        ? search
        : "";

  // =====================================
  // Table columns
  // =====================================
  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="medicalIcd-page-table-sort-name-btn"
        >
          Name
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => (
        <div
          className="text-center"
          data-testid={`medicalIcd-page-row-${row.original.id}-name`}
        >
          {row.getValue("name")}
        </div>
      ),
    },

    {
      accessorKey: "codes",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-bold text-black w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="medicalIcd-page-table-sort-icd-codes-btn"
        >
          ICD Codes
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),

      cell: ({ row }) => {
        const icds = row.getValue("codes") as {
          id: string;
          code: string;
        }[];

        if (!icds || !Array.isArray(icds) || icds.length === 0) {
          return (
            <span
              className="text-gray-400 text-center block"
              data-testid={`medicalIcd-page-row-${row.original.id}-codes-empty`}
            >
              -
            </span>
          );
        }

        const hasSearch = activeHighlightQuery.length >= 3;
        const displayIcds = icds.slice(0, 2);
        const remainingCount = icds.length - 2;

        return (
          <div
            className="flex flex-col items-center gap-1"
            data-testid={`medicalIcd-page-row-${row.original.id}-codes`}
          >
            <div className="flex flex-wrap justify-center items-center gap-1.5">
              {displayIcds.map((icd, idx) => {
                const isMatching =
                  hasSearch &&
                  icd.code.toLowerCase().includes(activeHighlightQuery);

                return (
                  <div
                    key={idx}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-1 transition-all ${
                      isMatching
                        ? "bg-yellow-50 border border-yellow-400"
                        : "bg-blue-50"
                    }`}
                    data-testid={`medicalIcd-page-row-${row.original.id}-code-${icd.id || idx}`}
                  >
                    <span
                      className={`text-xs font-medium ${
                        isMatching ? "text-yellow-900" : "text-blue-800"
                      }`}
                    >
                      {highlightMatch(icd.code, activeHighlightQuery)}
                    </span>
                  </div>
                );
              })}

              {remainingCount > 0 && (
                <button
                  onClick={() => {
                    setSelectedIcd(icds);
                    setIcdDialogOpen(true);
                  }}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  data-testid={`medicalIcd-page-row-${row.original.id}-more-icds-btn`}
                >
                  +{remainingCount} more
                </button>
              )}
            </div>
          </div>
        );
      },
    },

    {
      accessorKey: "isActive",
      header: () => (
        <div
          className="w-full font-bold text-black text-center normal-case"
          data-testid="medicalIcd-page-table-status-header"
        >
          Status
        </div>
      ),
      cell: ({ row }) => {
        const item = row.original;

        return (
          <div
            className="w-full flex justify-center"
            data-testid={`medicalIcd-page-row-${item.id}-status`}
          >
            <button
              onClick={() => toggleActive(item)}
              data-testid={`medicalIcd-page-row-${item.id}-status-toggle`}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                item.isActive ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  item.isActive ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        );
      },
    },

    {
      id: "view",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black text-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="medicalIcd-page-table-sort-view-btn"
        >
          View
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;

        return (
          <div
            className="w-full flex justify-center py-2"
            data-testid={`medicalIcd-page-row-${item.id}-view`}
          >
            <button
              onClick={() => handleOpenDialog(item)}
              className="text-blue-600 hover:text-blue-800"
              title="View Details"
              data-testid={`medicalIcd-page-row-${item.id}-view-btn`}
            >
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div data-testid="medicalIcd-page">
      <div className={styles.container} data-testid="medicalIcd-page-container">
        <h5 className={styles.title} data-testid="medicalIcd-page-title">
          <span
            className={styles.dialogIcon}
            data-testid="medicalIcd-page-title-icon"
          >
            <i className="fa-solid fa-file-medical"></i>
          </span>
          Medical ICD Grouping
        </h5>
        <FileUpload
          totalItems={totalItems}
          hasFilters={hasActiveFilters}
          exportParams={buildFilterParams()}
        />
      </div>

      {/* Search */}
      <div
        className={`${styles["compact-search-container"]} ${styles["mb-6"]}`}
        data-testid="medicalIcd-page-search-container"
      >
        <div
          className={styles["search-wrapper"]}
          data-testid="medicalIcd-page-search-wrapper"
        >
          <div
            className={styles["search-header-compact"]}
            data-testid="medicalIcd-page-search-header"
          >
            <div
              className={styles["header-info"]}
              data-testid="medicalIcd-page-search-header-info"
            >
              <i className="fa-solid fa-filter"></i>
              <span>Search Filters</span>
            </div>

            <button
              onClick={clearFilters}
              className={styles["btn-clear-compact"]}
              data-testid="medicalIcd-page-search-reset-btn"
            >
              <i className="fa-solid fa-rotate-left"></i>
              Reset
            </button>
          </div>

          <div
            className={styles["search-fields-compact"]}
            style={{ gridTemplateColumns: "repeat(3, 1fr)" }}
            data-testid="medicalIcd-page-search-fields"
          >
            {/* Group Name */}
            <div
              className={styles["field-compact"]}
              data-testid="medicalIcd-page-search-groupname"
            >
              <label
                className={styles["label-compact"]}
                data-testid="medicalIcd-page-search-groupname-label"
              >
                <i className="fa-solid fa-layer-group"></i>
                Group Name
              </label>

              <div className={styles["input-container-compact"]}>
                <input
                  type="text"
                  placeholder="Search groups..."
                  value={advancedSearchInput.groupName}
                  onChange={(e) =>
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      groupName: e.target.value,
                    })
                  }
                  className={styles["input-compact"]}
                  data-testid="medicalIcd-page-search-groupname-input"
                />
              </div>
            </div>

            {/* ICD Code */}
            <div
              className={styles["field-compact"]}
              data-testid="medicalIcd-page-search-icdcode"
            >
              <label
                className={styles["label-compact"]}
                data-testid="medicalIcd-page-search-icdcode-label"
              >
                <i className="fa-solid fa-code"></i>
                ICD Code
              </label>

              <div className={styles["input-container-compact"]}>
                <input
                  type="text"
                  placeholder="Min 3 characters..."
                  value={advancedSearchInput.icdCode}
                  onChange={(e) =>
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      icdCode: e.target.value,
                    })
                  }
                  className={`${styles["input-compact"]} ${
                    advancedSearchInput.icdCode.trim().length > 0 &&
                    advancedSearchInput.icdCode.trim().length < 3
                      ? styles["input-warning-compact"]
                      : ""
                  }`}
                  data-testid="medicalIcd-page-search-icdcode-input"
                />
              </div>

              {advancedSearchInput.icdCode.trim().length > 0 &&
                advancedSearchInput.icdCode.trim().length < 3 && (
                  <span
                    className="hint-compact"
                    data-testid="medicalIcd-page-search-icdcode-min-length-message"
                  >
                    <i className="fa-solid fa-exclamation-circle"></i>
                    Min 3 chars
                  </span>
                )}
            </div>

            {/* Status */}
            <div
              className={styles["field-compact"]}
              data-testid="medicalIcd-page-search-status"
            >
              <label
                className={styles["label-compact"]}
                data-testid="medicalIcd-page-search-status-label"
              >
                <i className="fa-solid fa-toggle-on"></i>
                Status
              </label>

              <div className={styles["select-container-compact"]}>
                <select
                  value={advancedSearchInput.status}
                  onChange={(e) =>
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      status: e.target.value,
                    })
                  }
                  className={styles["input-compact"]}
                  data-testid="medicalIcd-page-search-status-select"
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          <div
            className={styles["stats-compact"]}
            data-testid="medicalIcd-page-search-stats"
          >
            <i className="fa-solid fa-chart-bar"></i>

            <span
              className={styles["stats-text-compact"]}
              data-testid="medicalIcd-page-search-results-count"
            >
              Showing <strong>{allItems.length}</strong> of{" "}
              <strong>{totalItems}</strong> groups
            </span>

            {(advancedSearch.groupName ||
              advancedSearch.icdCode ||
              advancedSearch.status) && (
              <span
                className={styles["badge-filtered-compact"]}
                data-testid="medicalIcd-page-search-filtered-badge"
              >
                <i className="fa-solid fa-filter"></i> Filtered
              </span>
            )}
          </div>
        </div>
      </div>

      {isDialogOpen && selectedItem && (
        <dialog
          className={styles["modern-dialog"]}
          open
          data-testid="medicalIcd-page-dialog"
        >
          <div
            className={styles["dialog-container"]}
            data-testid="medicalIcd-page-dialog-container"
          >
            {/* Header */}
            <div
              className={styles["dialog-header"]}
              data-testid="medicalIcd-page-dialog-header"
            >
              <div
                className={styles["header-content"]}
                data-testid="medicalIcd-page-dialog-header-content"
              >
                <div
                  className={styles["header-icon"]}
                  data-testid="medicalIcd-page-dialog-header-icon"
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </div>

                <div>
                  <h2
                    className={styles["dialog-title"]}
                    data-testid="medicalIcd-page-dialog-title"
                  >
                    Group Details
                  </h2>

                  <p
                    className={styles["dialog-subtitle"]}
                    data-testid="medicalIcd-page-dialog-subtitle"
                  >
                    {selectedItem.name}
                  </p>
                </div>
              </div>

              <button
                className={styles["close-btn"]}
                onClick={handleCloseDialog}
                data-testid="medicalIcd-page-dialog-close-btn"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div
              className={styles["dialog-body"]}
              data-testid="medicalIcd-page-dialog-body"
            >
              <section
                className={styles.section}
                data-testid="medicalIcd-page-dialog-basic-info"
              >
                <h3
                  className={styles["section-title"]}
                  data-testid="medicalIcd-page-dialog-basic-info-title"
                >
                  Basic Information
                </h3>

                <div
                  className={styles["info-group"]}
                  data-testid="medicalIcd-page-dialog-basic-info-group"
                >
                  <div
                    className={styles["info-row"]}
                    data-testid="medicalIcd-page-dialog-row-id"
                  >
                    <span className={styles["row-label"]}>Group ID</span>
                    <span
                      className={styles["info-text"]}
                      data-testid="medicalIcd-page-dialog-group-id"
                    >
                      {selectedItem
                        ? (page - 1) * limit +
                          allItems.findIndex((i) => i.id === selectedItem.id) +
                          1
                        : ""}
                    </span>
                  </div>

                  <div
                    className={styles["info-row"]}
                    data-testid="medicalIcd-page-dialog-row-name"
                  >
                    <span className={styles["row-label"]}>Group Name</span>
                    <span
                      className={styles["info-text"]}
                      data-testid="medicalIcd-page-dialog-group-name"
                    >
                      {selectedItem.name}
                    </span>
                  </div>

                  <div
                    className={styles["info-row"]}
                    data-testid="medicalIcd-page-dialog-row-status"
                  >
                    <span className={styles["row-label"]}>Status</span>

                    <div
                      className={`${styles["status-badge-inline"]} ${
                        selectedItem.isActive ? styles.active : styles.inactive
                      }`}
                      data-testid="medicalIcd-page-dialog-status-badge"
                    >
                      <span className={styles["status-dot"]}></span>
                      {selectedItem.isActive ? "Active" : "Inactive"}
                    </div>
                  </div>
                </div>
              </section>

              {/* Codes */}
              <section
                className={`${styles.section} ${styles["full-width"]}`}
                data-testid="medicalIcd-page-dialog-codes-section"
              >
                <div
                  className={styles["section-header-flex"]}
                  data-testid="medicalIcd-page-dialog-codes-header"
                >
                  <h3
                    className={styles["section-title"]}
                    data-testid="medicalIcd-page-dialog-codes-title"
                  >
                    ICD Codes
                  </h3>

                  <span
                    className={styles["count-badge"]}
                    data-testid="medicalIcd-page-dialog-codes-count"
                  >
                    {selectedItem.codes?.length || 0}
                  </span>
                </div>

                <div
                  className={styles["Icd-scroll-area"]}
                  data-testid="medicalIcd-page-dialog-codes-scroll"
                >
                  {selectedItem.codes?.length ? (
                    <div
                      className={styles["Icd-grid"]}
                      data-testid="medicalIcd-page-dialog-codes-grid"
                    >
                      {selectedItem.codes.map((code, idx) => (
                        <div
                          key={idx}
                          className={styles["Icd-card-mini"]}
                          data-testid={`medicalIcd-page-dialog-code-${code.id || idx}`}
                        >
                          <div
                            className={styles["mini-icon"]}
                            data-testid={`medicalIcd-page-dialog-code-${code.id || idx}-icon`}
                          >
                            #
                          </div>

                          <span
                            className={styles["Icd-name"]}
                            data-testid={`medicalIcd-page-dialog-code-${code.id || idx}-value`}
                          >
                            {code.code}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      className={styles["empty-state-mini"]}
                      data-testid="medicalIcd-page-dialog-codes-empty"
                    >
                      No ICDs assigned
                    </div>
                  )}
                </div>
              </section>

              {/* Audit Trail / Logs */}
              <div
                style={{ padding: "20px 28px 8px" }}
                data-testid="medicalIcd-page-audit-trail"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 16,
                  }}
                  data-testid="medicalIcd-page-audit-trail-header"
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: "#f0fdf4",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #bbf7d0",
                    }}
                    data-testid="medicalIcd-page-audit-trail-icon"
                  >
                    <i
                      className="fa-solid fa-clock-rotate-left"
                      style={{ fontSize: 12, color: "#16a34a" }}
                    />
                  </div>

                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                    data-testid="medicalIcd-page-audit-trail-title"
                  >
                    Audit Trail
                  </span>
                </div>

                {(selectedItem as any).auditLog?.length > 0 && (
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      padding: "14px 16px",
                    }}
                    data-testid="medicalIcd-page-audit-history"
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 12,
                      }}
                      data-testid="medicalIcd-page-audit-history-header"
                    >
                      <i
                        className="fa-solid fa-pen-to-square"
                        style={{ fontSize: 11, color: "#10b981" }}
                      />

                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                        }}
                        data-testid="medicalIcd-page-audit-history-title"
                      >
                        Update History
                      </span>

                      <span
                        style={{
                          fontSize: 10,
                          padding: "1px 8px",
                          borderRadius: 9999,
                          background: "#f0fdf4",
                          color: "#15803d",
                          fontWeight: 600,
                          border: "1px solid #bbf7d0",
                          marginLeft: "auto",
                        }}
                        data-testid="medicalIcd-page-audit-update-count"
                      >
                        {(selectedItem as any).auditLog.length} update
                        {(selectedItem as any).auditLog.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                      data-testid="medicalIcd-page-audit-log-list"
                    >
                      {(selectedItem as any).auditLog
                        .slice(0, showAllLogs ? undefined : 4)
                        .map((log: any, i: number) => (
                          <div
                            key={log.id}
                            data-testid={`medicalIcd-page-audit-log-${log.id}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "8px 12px",
                              borderRadius: 8,
                              background: i === 0 ? "#f0fdf4" : "#f8fafc",
                              border: `1px solid ${
                                i === 0 ? "#bbf7d0" : "#e2e8f0"
                              }`,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                              data-testid={`medicalIcd-page-audit-log-${log.id}-actor-section`}
                            >
                              <div
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  background:
                                    i === 0
                                      ? "linear-gradient(135deg, #10b981, #059669)"
                                      : "linear-gradient(135deg, #94a3b8, #64748b)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#fff",
                                }}
                                data-testid={`medicalIcd-page-audit-log-${log.id}-avatar`}
                              >
                                {String(log.actorName ?? "?")[0].toUpperCase()}
                              </div>

                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#1e293b",
                                }}
                                data-testid={`medicalIcd-page-audit-log-${log.id}-actor`}
                              >
                                {log.actorName}
                              </span>

                              {i === 0 && (
                                <span
                                  style={{
                                    fontSize: 10,
                                    padding: "1px 8px",
                                    borderRadius: 9999,
                                    background: "#dcfce7",
                                    color: "#15803d",
                                    fontWeight: 600,
                                    border: "1px solid #bbf7d0",
                                  }}
                                  data-testid={`medicalIcd-page-audit-log-${log.id}-latest`}
                                >
                                  Latest
                                </span>
                              )}
                            </div>

                            <span
                              style={{
                                fontSize: 11,
                                color: "#64748b",
                                fontWeight: 500,
                              }}
                              data-testid={`medicalIcd-page-audit-log-${log.id}-date`}
                            >
                              {new Date(log.createdAt).toLocaleString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                        ))}

                      {(selectedItem as any).auditLog.length > 4 && (
                        <div
                          style={{ textAlign: "center", padding: "6px 0" }}
                          data-testid="medicalIcd-page-audit-show-more-container"
                        >
                          <button
                            onClick={() => setShowAllLogs((v) => !v)}
                            data-testid="medicalIcd-page-audit-show-more-btn"
                            style={{
                              fontSize: 11,
                              color: "#3b82f6",
                              fontWeight: 600,
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            {showAllLogs
                              ? "Show less ↑"
                              : `+${
                                  (selectedItem as any).auditLog.length - 4
                                } more updates ↓`}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div
              className={styles["dialog-footer"]}
              data-testid="medicalIcd-page-dialog-footer"
            >
              <button
                className={styles["btn-secondary"]}
                onClick={handleCloseDialog}
                data-testid="medicalIcd-page-dialog-footer-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </dialog>
      )}

      <Dialog
        open={IcdDialogOpen}
        onOpenChange={setIcdDialogOpen}
        data-testid="medicalIcd-page-icd-dialog"
      >
        <DialogContent
          className="max-w-lg modern-dialog bg-white icd-dialog"
          data-testid="medicalIcd-page-icd-dialog-content"
        >
          <DialogHeader
            className="dialog-header"
            data-testid="medicalIcd-page-icd-dialog-header"
          >
            <DialogTitle
              className="text-xl font-bold text-blue-900 flex items-center gap-2 dialog-title"
              data-testid="medicalIcd-page-icd-dialog-title"
            >
              <i className="fa-solid fa-list-check"></i>
              All ICDs
            </DialogTitle>
          </DialogHeader>

          <div
            className="mt-4 dialog-body"
            data-testid="medicalIcd-page-icd-dialog-body"
          >
            <div
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200 section"
              data-testid="medicalIcd-page-icd-dialog-section"
            >
              <div
                className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto pr-2 Icd-scroll-area"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "#3b82f6 #dbeafe",
                }}
                data-testid="medicalIcd-page-icd-dialog-codes-grid"
              >
                {selectedIcd &&
                  selectedIcd.map((icd, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-blue-100 hover:border-blue-300 hover:shadow-md transition-all Icd-card-mini"
                      data-testid={`medicalIcd-page-icd-dialog-code-${icd.id || index}`}
                    >
                      <div
                        className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full flex-shrink-0 mini-icon"
                        data-testid={`medicalIcd-page-icd-dialog-code-${icd.id || index}-icon`}
                      >
                        <i className="fa-solid fa-building text-sm"></i>
                      </div>

                      <span
                        className="text-gray-800 font-medium flex-1 Icd-name"
                        data-testid={`medicalIcd-page-icd-dialog-code-${icd.id || index}-value`}
                      >
                        {icd.code}
                      </span>
                    </div>
                  ))}
              </div>

              <div
                className="mt-4 pt-3 border-t border-blue-200"
                data-testid="medicalIcd-page-icd-dialog-total"
              >
                <p
                  className="text-sm text-gray-600 flex items-center gap-2"
                  data-testid="medicalIcd-page-icd-dialog-total-text"
                >
                  <i className="fa-solid fa-info-circle text-blue-600"></i>
                  <span>
                    Total:{" "}
                    <strong
                      className="text-blue-900"
                      data-testid="medicalIcd-page-icd-dialog-total-count"
                    >
                      {selectedIcd?.length || 0}
                    </strong>{" "}
                    ICDs
                  </span>
                </p>
              </div>
            </div>
          </div>

          <DialogFooter
            className="dialog-footer"
            data-testid="medicalIcd-page-icd-dialog-footer"
          >
            <Button
              onClick={() => setIcdDialogOpen(false)}
              className="bg-blue-600 hover:bg-blue-700 btn-secondary"
              data-testid="medicalIcd-page-icd-dialog-close-btn"
            >
              <i className="fa-solid fa-check mr-2"></i>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        data-testid="medicalIcd-page-warning-dialog"
        open={warningDialog.open}
        onOpenChange={(open) =>
          !open &&
          setWarningDialog({
            open: false,
            item: null,
            usageData: null,
            loadingUsage: false,
          })
        }
      >
        <DialogContent
          className="max-w-md bg-white p-0 overflow-hidden rounded-2xl flex flex-col max-h-[90vh]"
          data-testid="medicalIcd-page-warning-dialog-content"
        >
          {/* Header */}
          <div
            className="p-6 pb-2 flex-shrink-0"
            data-testid="medicalIcd-page-warning-dialog-header"
          >
            <DialogHeader>
              <DialogTitle
                className="flex items-center gap-2 text-amber-600 font-bold"
                data-testid="medicalIcd-page-warning-dialog-title"
              >
                <AlertTriangle className="h-5 w-5" />
                Warning : Group in use
              </DialogTitle>

              <DialogDescription
                className="text-slate-500 text-sm"
                data-testid="medicalIcd-page-warning-dialog-description"
              >
                Changing status to <strong>inactive</strong> may affect
                predefined rules.
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Main Content Area */}
          <div
            className="px-6 flex-1 min-h-0 flex flex-col"
            data-testid="medicalIcd-page-warning-dialog-body"
          >
            {warningDialog.usageData?.isUsed && (
              <div
                className={styles.alertContainer}
                data-testid="medicalIcd-page-warning-dialog-usage"
              >
                {/* Fixed Info Banner */}
                <div
                  className="rounded-lg bg-amber-50 p-3 border border-amber-100 flex-shrink-0"
                  data-testid="medicalIcd-page-warning-dialog-info-banner"
                >
                  <p
                    className="text-xs text-amber-800 font-medium text-center"
                    data-testid="medicalIcd-page-warning-dialog-usage-count"
                  >
                    Currently active in{" "}
                    <strong>{warningDialog.usageData.totalUsageCount}</strong>{" "}
                    location(s).
                  </p>
                </div>

                {/* Scrollable Section */}
                <div
                  className={styles.alertScrollArea}
                  data-testid="medicalIcd-page-warning-dialog-scroll-area"
                >
                  {/* ICD Rules */}
                  {warningDialog.usageData.icdValidationRules?.length > 0 && (
                    <div
                      className={styles.alertSection}
                      data-testid="medicalIcd-page-warning-dialog-icd-rules"
                    >
                      <h4
                        className={styles.alertSectionTitle}
                        data-testid="medicalIcd-page-warning-dialog-icd-rules-title"
                      >
                        ICD Rules
                      </h4>

                      {warningDialog.usageData.icdValidationRules.map(
                        (rule: any) => (
                          <div
                            key={rule.ruleId}
                            className={styles.alertCard}
                            data-testid={`medicalIcd-page-warning-dialog-icd-rule-${rule.ruleId}`}
                          >
                            <span
                              className="text-sm font-medium"
                              data-testid={`medicalIcd-page-warning-dialog-icd-rule-${rule.ruleId}-name`}
                            >
                              {rule.ruleName}
                            </span>

                            <span
                              className={styles.alertBadge}
                              data-testid={`medicalIcd-page-warning-dialog-icd-rule-${rule.ruleId}-type`}
                            >
                              {formatText(rule.ruleType)}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  )}

                  {/* Activity Rules */}
                  {warningDialog.usageData.activityValidationRules?.length >
                    0 && (
                    <div
                      className={styles.alertSection}
                      data-testid="medicalIcd-page-warning-dialog-activity-rules"
                    >
                      <h4
                        className={styles.alertSectionTitle}
                        data-testid="medicalIcd-page-warning-dialog-activity-rules-title"
                      >
                        Activity Rules
                      </h4>

                      {warningDialog.usageData.activityValidationRules.map(
                        (rule: any) => (
                          <div
                            key={rule.ruleId}
                            className={styles.alertActivityCard}
                            data-testid={`medicalIcd-page-warning-dialog-activity-rule-${rule.ruleId}`}
                          >
                            <span
                              className="text-sm font-medium"
                              data-testid={`medicalIcd-page-warning-dialog-activity-rule-${rule.ruleId}-name`}
                            >
                              {rule.ruleName}
                            </span>

                            <span
                              className={styles.alertActivityBadge}
                              data-testid={`medicalIcd-page-warning-dialog-activity-rule-${rule.ruleId}-type`}
                            >
                              {formatText(rule.ruleType)}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  )}

                  {/* Custom Groups */}
                  {warningDialog.usageData.customIcdGroups?.length > 0 && (
                    <div
                      className={styles.alertSection}
                      data-testid="medicalIcd-page-warning-dialog-custom-groups"
                    >
                      <h4
                        className={styles.alertSectionTitle}
                        data-testid="medicalIcd-page-warning-dialog-custom-groups-title"
                      >
                        Custom Groups
                      </h4>

                      {warningDialog.usageData.customIcdGroups.map(
                        (group: any) => (
                          <div
                            key={group.groupId}
                            className={styles.alertCustomGroupContainer}
                            data-testid={`medicalIcd-page-warning-dialog-custom-group-${group.groupId}`}
                          >
                            Group:{" "}
                            <strong
                              data-testid={`medicalIcd-page-warning-dialog-custom-group-${group.groupId}-name`}
                            >
                              {group.groupName}
                            </strong>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </div>

                <p
                  className={styles.alertFooterQuestion}
                  data-testid="medicalIcd-page-warning-dialog-question"
                >
                  Are you sure you want to proceed?
                </p>
              </div>
            )}
          </div>

          <div
            className="p-6 pt-4 flex-shrink-0 bg-white"
            data-testid="medicalIcd-page-warning-dialog-footer"
          >
            <DialogFooter className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl h-10 text-sm"
                onClick={() =>
                  setWarningDialog((prev) => ({ ...prev, open: false }))
                }
                data-testid="medicalIcd-page-warning-dialog-cancel-btn"
              >
                Cancel
              </Button>

              <Button
                className="flex-1 rounded-xl h-10 bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm"
                onClick={() => {
                  confirmToggle(warningDialog.item!);
                  setWarningDialog((prev) => ({ ...prev, open: false }));
                }}
                data-testid="medicalIcd-page-warning-dialog-proceed-btn"
              >
                Yes, Proceed
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster data-testid="medicalIcd-page-toaster" />

      <div
        className={styles.tableWrapper}
        data-testid="medicalIcd-page-table-wrapper"
      >
        <DataTable
          data={allItems}
          columns={columns}
          isLoading={loading}
          onDownload={handleDownload}
          addButtonLabel="Add New Group"
          pageIndex={page - 1}
          pageSize={limit}
          totalPages={totalPages}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(size) => {
            setLimit(size);
            setPage(1);
          }}
          globalSearch={searchInput}
          onGlobalSearchChange={setSearchInput}
          data-testid="medicalicd-page-datatable"
        />
      </div>
    </div>
  );
};

export default Medicalicd;